/**
 * Configurações, constantes e padrões do sistema
 */

// Configurações de geocodificação
export interface GeocodingConfig {
  addressCepToleranceMeters: number; // Distância máxima entre endereço e CEP
  radiusVerificationMeters: number; // Raio para verificação por endereço
  cepAreaMultiplier: number; // Multiplicador para área do CEP
  enableHybridStrategy: boolean; // Ativar estratégia híbrida
}

// Padrões de detecção de endereço
export interface AddressPatterns {
  cep: RegExp[];
  brazilianStates: RegExp;
  brazilianIndicators: RegExp[];
}

// Constantes para detecção de padrões
export const ADDRESS_PATTERNS: AddressPatterns = {
  cep: [
    /^\d{5}-?\d{3}$/,           // 12345-678 ou 12345678
    /^\d{2}\.\d{3}-\d{3}$/,     // 12.345-678
    /\bCEP:?\s*(\d{5}-?\d{3})\b/i, // CEP: 12345-678
    /\b\d{5}-?\d{3}\b/          // CEP em qualquer lugar do texto
  ],
  brazilianStates: /\b(AC|AL|AP|AM|BA|CE|DF|ES|GO|MA|MT|MS|MG|PA|PB|PR|PE|PI|RJ|RN|RS|RO|RR|SC|SP|SE|TO)\b/i,
  brazilianIndicators: [
    /\bBrasil\b/i,
    /\b\d{5}-?\d{3}\b/, // Padrão de CEP
    /\b(São Paulo|Rio de Janeiro|Belo Horizonte|Salvador|Brasília|Fortaleza|Manaus|Curitiba|Recife|Porto Alegre)\b/i
  ]
};

// Configuração padrão para geocodificação
export const DEFAULT_GEOCODING_CONFIG: GeocodingConfig = {
  addressCepToleranceMeters: 2000, // 2km de tolerância entre endereço e CEP
  radiusVerificationMeters: 500,   // 500m de raio para verificação
  cepAreaMultiplier: 1.5,          // 1.5x a área estimada do CEP
  enableHybridStrategy: true       // Ativar estratégia híbrida por padrão
};

// Configuração de APIs
export interface ApiConfig {
  openCage: {
    baseUrl: string;
    rateLimit: number; // requisições por dia
  };
  viaCep: {
    baseUrl: string;
    rateLimit: number; // requisições por minuto
  };
}

// Configuração padrão das APIs
export const DEFAULT_API_CONFIG: ApiConfig = {
  openCage: {
    baseUrl: 'https://api.opencagedata.com/geocode/v1/json',
    rateLimit: 2500 // por dia
  },
  viaCep: {
    baseUrl: 'https://viacep.com.br/ws',
    rateLimit: 200 // por minuto (estimativa conservadora)
  }
};

// Timeouts em milissegundos (valores aumentados para melhor estabilidade)
export const TIMEOUTS = {
  GEOCODING: 20000,      // Aumentado de 10s para 20s para geocodificação
  CONSISTENCY_CHECK: 30000, // Aumentado de 15s para 30s para verificação de consistência
  PROXIMITY_CHECK: 10000, // Aumentado de 5s para 10s para verificação de proximidade
  GEOLOCATION: 90000     // Novo: 90s para operações de geolocalização
} as const;

// Limites geográficos do Brasil
export const BRAZIL_BOUNDS = {
  north: 5.2717,    // Ponto mais ao norte (Roraima)
  south: -33.7683,  // Ponto mais ao sul (Rio Grande do Sul)
  east: -28.8479,   // Ponto mais a leste (Fernando de Noronha)
  west: -73.9872,   // Ponto mais a oeste (Acre)

  // Coordenadas aproximadas do centro geográfico do Brasil
  center: {
    latitude: -14.2350,
    longitude: -51.9253
  }
} as const;

// Configurações de validação geográfica
export interface GeographicValidationConfig {
  enableBrazilValidation: boolean;
  allowNearBorderTolerance: number; // metros de tolerância nas fronteiras
  warnOnSuspiciousDistance: number; // metros para avisar sobre distâncias suspeitas
}

export const DEFAULT_GEOGRAPHIC_VALIDATION_CONFIG: GeographicValidationConfig = {
  enableBrazilValidation: true,
  allowNearBorderTolerance: 50000, // 50km de tolerância nas fronteiras
  warnOnSuspiciousDistance: 1000000 // 1000km - avisa se muito longe do centro
};